/**
 * Mein Paket – Dynamisches Bundle
 * Nutzer wählt beliebige Barometer, ab 3 = 10% Rabatt, ab 5 = 15% Rabatt
 */
import { useState, useRef, useEffect } from "react";
import { applySeo } from "@/lib/seo";
import { AgencyLogo } from "@/components/AgencyLogo";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { BAROMETER_PRICES } from "@shared/prices";

const LOGO_URL = "https://cdn.manus.space/webdev-static-assets/logo-larissa.png";

const C = {
  accent: "#6d0000",
  teal: "#115751",
  muted: "#9ca3af",
};

interface Barometer {
  id: string;
  productId: string;
  icon: string;
  name: string;
  shortName: string;
  price: number;
  color: string;
  path: string;
  tags: string[];
  desc: string;
}

const ALL_BAROMETER: Barometer[] = [
  {
    id: "sensorik",
    productId: "sensorik_analyse",
    icon: "/manus-storage/icon_onpage_grundlagen_a1ddd99a.png",
    name: "Sensorik-Barometer™",
    shortName: "Sensorik",
    price: BAROMETER_PRICES.sensorik,
    color: "#6d0000",
    path: "/sensorik",
    tags: ["Design", "Wirkung", "Farben"],
    desc: "Visuelle Wirkung, Farbpsychologie, Typografie, Bildsprache, multisensorisches Erlebnis",
  },
  {
    id: "lokal",
    productId: "lokal_analyse",
    icon: "/manus-storage/1_55991aa3.svg",
    name: "Lokal-Barometer™",
    shortName: "Lokal",
    price: BAROMETER_PRICES.lokal,
    color: "#115751",
    path: "/lokal",
    tags: ["Google Maps", "Bewertungen", "Lokal"],
    desc: "Google Business Profile, lokale Sichtbarkeit, NAP-Konsistenz, Bewertungen",
  },
  {
    id: "geo",
    productId: "geo_analyse",
    icon: "/manus-storage/7_8bcad2d2.svg",
    name: "GEO-Barometer™",
    shortName: "GEO / KI",
    price: BAROMETER_PRICES.geo,
    color: "#1a3a5c",
    path: "/geo-barometer",
    tags: ["ChatGPT", "KI-Sichtbarkeit", "E-E-A-T"],
    desc: "KI-Sichtbarkeit, strukturierte Daten, E-E-A-T, ChatGPT/Perplexity-Optimierung",
  },
  {
    id: "seo",
    productId: "seo_analyse",
    icon: "/manus-storage/23_1ab7147e.svg",
    name: "SEO-Barometer™",
    shortName: "SEO",
    price: BAROMETER_PRICES.seo,
    color: "#0a5c36",
    path: "/seo-barometer",
    tags: ["On-Page SEO", "Core Web Vitals", "Keywords"],
    desc: "SEO-Grundlagen-Check: On-Page SEO, technische SEO, Core Web Vitals – kein Backlink-Check",
  },
  {
    id: "conversion",
    productId: "conversion_analyse",
    icon: "/manus-storage/33_d51b7819.svg",
    name: "Conversion-Barometer™",
    shortName: "Conversion",
    price: BAROMETER_PRICES.conversion,
    color: "#b45309",
    path: "/conversion-barometer",
    tags: ["CTAs", "Vertrauen", "Psychologie"],
    desc: "CTAs, Vertrauen, Reibungsfreiheit, Preispsychologie, Cialdini-Prinzipien",
  },
  {
    id: "accessibility",
    productId: "accessibility_analyse",
    icon: "/manus-storage/16_9c3f7718.svg",
    name: "Accessibility-Barometer™",
    shortName: "Accessibility",
    price: BAROMETER_PRICES.accessibility,
    color: "#3730a3",
    path: "/accessibility-barometer",
    tags: ["WCAG 2.2", "EAA", "Barrierefreiheit"],
    desc: "WCAG 2.2-Konformität, ARIA-Rollen, Tastaturnavigation, European Accessibility Act",
  },
  {
    id: "datenschutz",
    productId: "datenschutz_analyse",
    icon: "/manus-storage/29_7f3bb394.svg",
    name: "Datenschutz-Barometer™",
    shortName: "Datenschutz",
    price: BAROMETER_PRICES.datenschutz,
    color: "#0f766e",
    path: "/datenschutz-barometer",
    tags: ["DSGVO", "Cookie-Consent", "Impressum"],
    desc: "DSGVO-Konformität, Cookie-Consent, Impressum, SSL, externe Dienste",
  },
  {
    id: "ecommerce",
    productId: "ecommerce_analyse",
    icon: "/manus-storage/21_453003f0.svg",
    name: "E-Commerce-Barometer™",
    shortName: "E-Commerce",
    price: BAROMETER_PRICES.ecommerce,
    color: "#4f46e5",
    path: "/ecommerce-barometer",
    tags: ["Shop", "Checkout", "Vertrauen"],
    desc: "Produktpräsentation, Checkout-Optimierung, Vertrauenssignale, mobile Conversion",
  },
  {
    id: "wettbewerb",
    productId: "wettbewerb_analyse",
    icon: "/manus-storage/41_3269a7aa.svg",
    name: "Wettbewerbs-Barometer™",
    shortName: "Wettbewerb",
    price: BAROMETER_PRICES.wettbewerb,
    color: "#7c2d12",
    path: "/wettbewerb-barometer",
    tags: ["Konkurrenz", "Benchmarks", "Positionierung"],
    desc: "Vergleich mit bis zu 3 Wettbewerbern, Stärken/Schwächen-Analyse, Benchmarks",
  },
];

function getDiscount(count: number): number {
  if (count >= 5) return 15;
  if (count >= 3) return 10;
  return 0;
}

function getDiscountLabel(count: number): string {
  if (count >= 5) return "15% Bundle-Rabatt";
  if (count >= 3) return "10% Bundle-Rabatt";
  if (count === 2) return "Noch 1 Barometer für 10% Rabatt";
  if (count === 1) return "Noch 2 Barometer für 10% Rabatt";
  return "Ab 3 Barometern: 10% Rabatt";
}

export default function MeinPaket() {
  useEffect(() => applySeo({
    title: "Mein Paket – Barometer-Bundle mit Rabatt | WERBEAGENTUR Larissa Girenko",
    description: "Wähle 3 oder mehr Barometer-Analysen und spare bis zu 15 %. Dein individuelles Analyse-Paket ab 97 € pro Barometer.",
    path: "/mein-paket",
  }), []);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [redeemedTokens, setRedeemedTokens] = useState<Array<{ token: string; productId: string }> | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const createBundleCheckout = trpc.payment.createBundleCheckout.useMutation();
  const redeemWithCode = trpc.payment.redeemWithCode.useMutation();

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedBarometer = ALL_BAROMETER.filter(b => selected.has(b.id));
  const count = selectedBarometer.length;
  const discountPercent = getDiscount(count);
  const subtotal = selectedBarometer.reduce((s, b) => s + b.price, 0);
  const discountAmount = Math.round(subtotal * discountPercent / 100);
  const total = subtotal - discountAmount;

  const PRODUCT_URL_MAP: Record<string, string> = {
    sensorik_analyse: "/sensorik",
    lokal_analyse: "/lokal",
    geo_analyse: "/geo-barometer",
    seo_analyse: "/seo-barometer",
    conversion_analyse: "/conversion-barometer",
    accessibility_analyse: "/accessibility-barometer",
    datenschutz_analyse: "/datenschutz-barometer",
    ecommerce_analyse: "/ecommerce-barometer",
    wettbewerb_analyse: "/wettbewerb-barometer",
  };

  const handleCodeRedeem = async () => {
    if (count === 0) {
      setCodeError("Bitte wähle zuerst mindestens ein Barometer aus.");
      return;
    }
    setCodeError(null);
    try {
      const productIds = selectedBarometer.map(b => b.productId);
      const result = await redeemWithCode.mutateAsync({ code: codeInput, productIds });
      if (result.success && result.tokens) {
        setRedeemedTokens(result.tokens);
        toast.success("Code eingelöst! Deine Analysen sind freigeschaltet.");
      } else {
        setCodeError((result as any).reason ?? "Code ungültig");
      }
    } catch (err: any) {
      setCodeError(err.message ?? "Fehler beim Einlösen");
    }
  };

  const handleCheckout = async () => {
    if (count === 0) {
      toast.error("Bitte wähle mindestens ein Barometer aus.");
      return;
    }
    setIsLoading(true);
    try {
      const productIds = selectedBarometer.map(b => b.productId) as Parameters<typeof createBundleCheckout.mutateAsync>[0]["productIds"];
      const result = await createBundleCheckout.mutateAsync({
        origin: window.location.origin,
        productIds,
        returnPath: "/mein-paket",
      });
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      toast.error("Fehler beim Erstellen der Bestellung. Bitte versuche es erneut.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Erfolgsseite nach Code-Einlösung
  if (redeemedTokens) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <header className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 no-underline">
            <AgencyLogo withLogo size="sm" />
          </a>
        </header>
        <section className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="text-5xl mb-6">✓</div>
          <h1 className="font-black uppercase mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 6vw, 3.5rem)", color: C.accent }}>
            ANALYSEN FREIGESCHALTET!
          </h1>
          <p className="text-gray-500 mb-10">Deine Barometer sind bereit. Klicke auf einen Link, um die Analyse zu starten.</p>
          <div className="space-y-3 text-left">
            {redeemedTokens.map(({ token, productId }) => {
              const barometer = ALL_BAROMETER.find(b => b.productId === productId);
              const url = PRODUCT_URL_MAP[productId];
              if (!barometer || !url) return null;
              return (
                <a
                  key={token}
                  href={`${url}?token=${token}`}
                  className="flex items-center justify-between p-4 border-2 no-underline transition-all hover:border-opacity-80"
                  style={{ borderColor: barometer.color, backgroundColor: `${barometer.color}0d` }}
                >
                  <div className="flex items-center gap-3">
                    <img src={barometer.icon} alt="" className="w-7 h-7 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <div>
                      <p className="font-black uppercase text-sm" style={{ fontFamily: "'Bebas Neue', sans-serif", color: barometer.color }}>{barometer.name}</p>
                      <p className="text-xs text-gray-500">{barometer.desc.slice(0, 60)}…</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 text-white" style={{ backgroundColor: barometer.color }}>Analyse starten →</span>
                </a>
              );
            })}
          </div>
          <button onClick={() => { setRedeemedTokens(null); setSelected(new Set()); setCodeInput(""); }}
            className="mt-8 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-gray-700 transition-colors">
            Weitere Analysen auswählen
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <header className="border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
        <a href="/" className="flex items-center gap-3 no-underline">
          <img src={LOGO_URL} alt="WERBEAGENTUR Larissa Girenko" className="w-8 h-8 object-contain" />
          <div>
            <span className="font-black uppercase text-black text-sm tracking-widest" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              WERBEAGENTUR Larissa Girenko
            </span>
            <p className="text-gray-400 text-xs hidden md:block">Marketing Barometer Suite</p>
          </div>
        </a>
        <a href="/" className="text-xs font-bold tracking-widest uppercase px-3 py-1.5 border transition-colors"
          style={{ color: "#6b7280", borderColor: "#e5e7eb" }}>← Übersicht</a>
      </header>

      {/* Hero */}
      <section className="bg-black text-white py-14 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent }}>
            Dein individuelles Paket
          </p>
          <h1 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.5rem, 7vw, 4.5rem)" }}>
            MEIN PAKET™
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Wähle genau die Barometer, die du brauchst. Ab 3 Analysen sparst du automatisch 10%, ab 5 Analysen 15%.
          </p>
          {/* Rabatt-Indikatoren */}
          <div className="flex items-center justify-center gap-6 mt-8">
            <div className={`text-center px-4 py-2 border transition-all ${count >= 3 ? "border-amber-400 bg-amber-400/10" : "border-gray-700"}`}>
              <div className={`font-black text-xl ${count >= 3 ? "text-amber-400" : "text-gray-500"}`} style={{ fontFamily: "'Bebas Neue', sans-serif" }}>10%</div>
              <div className={`text-xs uppercase tracking-widest ${count >= 3 ? "text-amber-300" : "text-gray-600"}`}>ab 3 Analysen</div>
            </div>
            <div className={`text-center px-4 py-2 border transition-all ${count >= 5 ? "border-green-400 bg-green-400/10" : "border-gray-700"}`}>
              <div className={`font-black text-xl ${count >= 5 ? "text-green-400" : "text-gray-500"}`} style={{ fontFamily: "'Bebas Neue', sans-serif" }}>15%</div>
              <div className={`text-xs uppercase tracking-widest ${count >= 5 ? "text-green-300" : "text-gray-600"}`}>ab 5 Analysen</div>
            </div>
          </div>
        </div>
      </section>

      {/* Barometer-Auswahl */}
      <section className="max-w-5xl mx-auto px-6 py-12 w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.8rem" }}>
              BAROMETER AUSWÄHLEN
            </h2>
            <p className="text-sm text-gray-500 mt-1">{count > 0 ? `${count} ausgewählt · ${getDiscountLabel(count)}` : "Klicke auf ein Barometer um es auszuwählen"}</p>
          </div>
          {count > 0 && (
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-gray-700 transition-colors"
            >
              Auswahl zurücksetzen
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" style={{ paddingBottom: count > 0 ? '80px' : '0' }}>
          {ALL_BAROMETER.map(b => {
            const isSelected = selected.has(b.id);
            return (
              <button
                key={b.id}
                onClick={() => toggle(b.id)}
                className="text-left border-2 p-5 transition-all focus:outline-none"
                style={{
                  borderColor: isSelected ? b.color : "#e5e7eb",
                  backgroundColor: isSelected ? `${b.color}0d` : "#fff",
                  cursor: "pointer",
                }}
              >
                {/* Akzentlinie */}
                <div className="h-0.5 w-full mb-4 -mt-5 -mx-5" style={{ width: "calc(100% + 2.5rem)", backgroundColor: isSelected ? b.color : "transparent", marginLeft: "-1.25rem", marginRight: "-1.25rem", marginTop: "-1.25rem", marginBottom: "1rem" }} />

                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <img src={b.icon} alt="" className="w-7 h-7 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <div
                      className="w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 transition-all"
                      style={{ borderColor: isSelected ? b.color : "#d1d5db", backgroundColor: isSelected ? b.color : "transparent" }}
                    >
                      {isSelected && <span className="text-white text-xs font-bold">✓</span>}
                    </div>
                  </div>
                  <span className="font-black text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif", color: b.color }}>{b.price} €</span>
                </div>

                <h3 className="font-black uppercase mb-1 leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.1rem", color: isSelected ? b.color : "#111" }}>
                  {b.name}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed mb-3">{b.desc}</p>
                <div className="flex flex-wrap gap-1">
                  {b.tags.map(tag => (
                    <span key={tag} className="text-xs px-2 py-0.5 font-bold uppercase tracking-widest"
                      style={{ backgroundColor: isSelected ? `${b.color}1a` : "#f3f4f6", color: isSelected ? b.color : "#6b7280" }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        {/* Desktop: Preisübersicht + CTA (inline, nicht sticky) */}
        {count > 0 && (
          <div className="hidden md:block border-2 p-8 bg-white" style={{ borderColor: C.accent }}>
            <div className="flex items-start md:items-center justify-between gap-6">
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.accent }}>Dein Paket</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedBarometer.map(b => (
                    <span key={b.id} className="text-xs font-bold px-2 py-1 text-white uppercase tracking-widest"
                      style={{ backgroundColor: b.color }}>
                      {b.shortName}
                    </span>
                  ))}
                </div>
                <div className="flex items-baseline gap-3">
                  {discountPercent > 0 && (
                    <span className="text-gray-400 line-through text-base">{subtotal} €</span>
                  )}
                  <span className="font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "2.5rem", color: C.accent }}>
                    {total} €
                  </span>
                  {discountPercent > 0 && (
                    <span className="text-xs font-bold px-2 py-1 text-white" style={{ backgroundColor: "#16a34a" }}>
                      {discountPercent}% gespart
                    </span>
                  )}
                </div>
                {discountPercent === 0 && count < 3 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {count === 1 ? "Noch 2 Barometer für 10% Rabatt" : "Noch 1 Barometer für 10% Rabatt"}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1">Einmalig · kein Abo · inkl. MwSt.</p>
              </div>
              <div className="flex-shrink-0">
                <button
                  onClick={handleCheckout}
                  disabled={isLoading}
                  className="px-8 py-4 font-black uppercase tracking-widest text-white transition-opacity disabled:opacity-60"
                  style={{ backgroundColor: C.teal, fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.1rem" }}
                >
                  {isLoading ? "Wird vorbereitet …" : `Jetzt kaufen – ${total} €`}
                </button>
                <p className="text-xs text-gray-400 mt-2 text-center">Sichere Zahlung via Stripe</p>
              </div>
            </div>
          </div>
        )}

        {/* Zugangscode-Feld */}
        {count > 0 && (
          <div className="mt-6 border border-gray-200 p-5 bg-[#f9f9f9]">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Hast du einen Zugangscode?</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={codeInput}
                onChange={e => { setCodeInput(e.target.value.toUpperCase()); setCodeError(null); }}
                onKeyDown={e => e.key === "Enter" && handleCodeRedeem()}
                placeholder="Z.B. LARISSA-TEST-2026"
                className="flex-1 border border-gray-300 px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:border-gray-500"
                style={{ letterSpacing: "0.05em" }}
              />
              <button
                onClick={handleCodeRedeem}
                disabled={redeemWithCode.isPending || !codeInput.trim()}
                className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-white transition-opacity disabled:opacity-50"
                style={{ backgroundColor: C.teal }}
              >
                {redeemWithCode.isPending ? "…" : "Einlösen"}
              </button>
            </div>
            {codeError && <p className="text-xs text-red-600 mt-2">{codeError}</p>}
          </div>
        )}

        {count === 0 && (
          <div className="border-2 border-dashed border-gray-200 p-8 text-center">
            <p className="text-gray-400 text-sm">Noch keine Barometer ausgewählt. Klicke oben auf die Karten, um dein Paket zusammenzustellen.</p>
          </div>
        )}
      </section>

      {/* Rabatt-Erklärung */}
      <section className="bg-[#f2f2f2] py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-black uppercase text-center mb-8" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>
            SO FUNKTIONIERT DER BUNDLE-RABATT
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 border border-gray-200">
              <div className="font-black text-4xl mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#6b7280" }}>1–2</div>
              <div className="text-xs font-bold uppercase tracking-widest mb-2 text-gray-500">Analysen</div>
              <div className="font-black text-2xl mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Normaler Preis</div>
              <p className="text-xs text-gray-500">Zahle den regulären Einzelpreis je Barometer.</p>
            </div>
            <div className="bg-white p-6 border-2" style={{ borderColor: "#b45309" }}>
              <div className="font-black text-4xl mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#b45309" }}>3–4</div>
              <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: "#b45309" }}>Analysen</div>
              <div className="font-black text-2xl mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>10% Rabatt</div>
              <p className="text-xs text-gray-500">Wird automatisch beim Checkout abgezogen.</p>
            </div>
            <div className="bg-white p-6 border-2" style={{ borderColor: "#16a34a" }}>
              <div className="font-black text-4xl mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#16a34a" }}>5–9</div>
              <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: "#16a34a" }}>Analysen</div>
              <div className="font-black text-2xl mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>15% Rabatt</div>
              <p className="text-xs text-gray-500">Maximale Ersparnis – fast das Komplett Website-RealitätsCheck.</p>
            </div>
          </div>
          <p className="text-center text-xs text-gray-400 mt-6">
            Tipp: Alle 9 Barometer gibt es als{" "}
            <a href="/komplett-paket" className="font-bold underline" style={{ color: C.accent }}>Komplett Website-RealitätsCheck™</a>{" "}
            mit 28% Rabatt – das beste Angebot.
          </p>
        </div>
      </section>

      {/* Mobile: Sticky Footer (nur auf kleinen Screens) */}
      {count > 0 && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2" style={{ borderColor: C.accent }}>
          {/* Ausgeklappter Bereich */}
          {summaryExpanded && (
            <div className="px-4 pt-4 pb-2 border-b border-gray-100">
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedBarometer.map(b => (
                  <span key={b.id} className="text-xs font-bold px-2 py-0.5 text-white uppercase tracking-widest"
                    style={{ backgroundColor: b.color }}>
                    {b.shortName}
                  </span>
                ))}
              </div>
              {discountPercent > 0 && (
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-gray-400 line-through text-sm">{subtotal} €</span>
                  <span className="text-xs font-bold px-2 py-0.5 text-white" style={{ backgroundColor: "#16a34a" }}>
                    {discountPercent}% gespart
                  </span>
                </div>
              )}
              {discountPercent === 0 && count < 3 && (
                <p className="text-xs text-gray-500 mb-1">
                  {count === 1 ? "Noch 2 Barometer für 10% Rabatt" : "Noch 1 Barometer für 10% Rabatt"}
                </p>
              )}
              <p className="text-xs text-gray-400 mb-2">Einmalig · kein Abo · inkl. MwSt.</p>
            </div>
          )}
          {/* Kompakte Leiste: immer sichtbar */}
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <button
              onClick={() => setSummaryExpanded(prev => !prev)}
              className="flex items-center gap-2 flex-1 min-w-0"
            >
              <div className="text-left">
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.accent }}>
                  {count} Barometer ausgewählt
                </p>
                <p className="font-black leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.6rem", color: C.accent }}>
                  {total} €
                </p>
              </div>
              <span className="text-gray-400 text-xs ml-1">{summaryExpanded ? "▼" : "▲"}</span>
            </button>
            <button
              onClick={handleCheckout}
              disabled={isLoading}
              className="flex-shrink-0 px-5 py-3 font-black uppercase tracking-widest text-white text-sm transition-opacity disabled:opacity-60"
              style={{ backgroundColor: C.teal, fontFamily: "'Bebas Neue', sans-serif" }}
            >
              {isLoading ? "…" : `Kaufen – ${total} €`}
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 px-6" style={{ paddingBottom: count > 0 ? "calc(2rem + 80px)" : "2rem" }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="WERBEAGENTUR Larissa Girenko" className="w-8 h-8 object-contain" />
            <span className="text-xs font-bold uppercase tracking-widest text-gray-500" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              WERBEAGENTUR Larissa Girenko
            </span>
          </div>
          <div className="flex gap-6">
            <a href="/impressum" className="text-xs text-gray-400 hover:text-gray-700 uppercase tracking-widest transition-colors">Impressum</a>
            <a href="/datenschutz" className="text-xs text-gray-400 hover:text-gray-700 uppercase tracking-widest transition-colors">Datenschutz</a>
            <a href="/" className="text-xs text-gray-400 hover:text-gray-700 uppercase tracking-widest transition-colors">Alle Barometer</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
